/**
 * Schedule Editor Page
 *
 * Main page component for the Schedule Visual Editor.
 * Integrates all schedule editing components into a cohesive interface.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useScheduleStore, useSelectedSchedule, useSelectedPeriod } from './scheduleStore';
import { useIDFStore } from '../stores/idfStore';
import { ScheduleBrowser } from './ScheduleBrowser';
import { DayEditor } from './DayEditor';
import { WeekEditor } from './WeekEditor';
import { YearEditor } from './YearEditor';
import { ScheduleGraph } from './ScheduleGraph';
import { TemplateLibrary } from './TemplateLibrary';
import { SchedulePropertiesPanel } from './SchedulePropertiesPanel';
import { ResizablePanel } from '../components/ResizablePanel';
import { useIsMobile } from '../hooks/useMediaQuery';
import type { ScheduleView, ScheduleTemplate, DaySchedule } from './types';

/**
 * Confirmation dialog component
 */
function ConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel = 'Confirm',
  confirmVariant = 'primary',
}: {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmLabel?: string;
  confirmVariant?: 'primary' | 'destructive';
}) {
  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-dialog-title"
    >
      <div className="w-full max-w-sm rounded-lg bg-popover shadow-xl">
        <div className="px-6 py-4">
          <h3 id="confirm-dialog-title" className="text-lg font-semibold text-foreground">
            {title}
          </h3>
          <p className="mt-2 text-sm text-muted-foreground">{message}</p>
        </div>

        <div className="flex justify-end gap-3 border-t border-border px-6 py-4">
          <button
            onClick={onClose}
            className="rounded-md border border-border px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring"
            autoFocus={confirmVariant === 'destructive'}
          >
            Cancel
          </button>
          <button
            onClick={() => {
              onConfirm();
              onClose();
            }}
            className={`rounded-md px-4 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-ring ${
              confirmVariant === 'destructive'
                ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90'
                : 'bg-primary text-primary-foreground hover:bg-primary/90'
            }`}
            autoFocus={confirmVariant !== 'destructive'}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Prompt dialog component
 */
function PromptDialog({
  isOpen,
  onClose,
  onSubmit,
  title,
  label,
  defaultValue = '',
  submitLabel = 'Submit',
}: {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (value: string) => void;
  title: string;
  label: string;
  defaultValue?: string;
  submitLabel?: string;
}) {
  const [value, setValue] = useState(defaultValue);

  // Reset value when dialog opens with new default
  useEffect(() => {
    if (isOpen) {
      setValue(defaultValue);
    }
  }, [isOpen, defaultValue]);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (value.trim()) {
      onSubmit(value.trim());
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="prompt-dialog-title"
    >
      <form onSubmit={handleSubmit} className="w-full max-w-sm rounded-lg bg-popover shadow-xl">
        <div className="px-6 py-4">
          <h3 id="prompt-dialog-title" className="text-lg font-semibold text-foreground">
            {title}
          </h3>
          <label className="mt-4 block">
            <span className="text-sm font-medium text-foreground">{label}</span>
            <input
              type="text"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
              autoFocus
            />
          </label>
        </div>

        <div className="flex justify-end gap-3 border-t border-border px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-border px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!value.trim()}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:bg-muted disabled:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          >
            {submitLabel}
          </button>
        </div>
      </form>
    </div>
  );
}

/**
 * View mode tabs
 */
function ViewTabs({
  view,
  onViewChange,
  compact = false,
}: {
  view: ScheduleView;
  onViewChange: (view: ScheduleView) => void;
  compact?: boolean;
}) {
  return (
    <div className="flex rounded-lg border border-border bg-muted p-1" role="tablist" aria-label="Schedule view">
      {(['day', 'week', 'year'] as ScheduleView[]).map((v) => (
        <button
          key={v}
          onClick={() => onViewChange(v)}
          role="tab"
          aria-selected={view === v}
          aria-controls={`${v}-panel`}
          className={`rounded-md ${compact ? 'px-2 py-1 text-xs' : 'px-4 py-1.5 text-sm'} font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-inset ${
            view === v
              ? 'bg-background text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          {v.charAt(0).toUpperCase() + v.slice(1)}
        </button>
      ))}
    </div>
  );
}

/**
 * Toolbar component
 */
function Toolbar({
  canUndo,
  canRedo,
  onUndo,
  onRedo,
  view,
  onViewChange,
  isDirty,
  onSave,
  isMobile,
  onOpenBrowser,
  onOpenProperties,
}: {
  canUndo: boolean;
  canRedo: boolean;
  onUndo: () => void;
  onRedo: () => void;
  view: ScheduleView;
  onViewChange: (view: ScheduleView) => void;
  isDirty: boolean;
  onSave: () => void;
  isMobile?: boolean;
  onOpenBrowser?: () => void;
  onOpenProperties?: () => void;
}) {
  return (
    <div className="flex items-center justify-between border-b bg-card px-2 md:px-4 py-2 gap-2">
      <div className="flex items-center gap-2 md:gap-4">
        {/* Mobile: Browser toggle */}
        {isMobile && onOpenBrowser && (
          <button
            onClick={onOpenBrowser}
            className="rounded p-1.5 text-muted-foreground hover:bg-muted"
            title="Schedule Browser"
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 6h18M3 12h18M3 18h18" />
            </svg>
          </button>
        )}

        {/* Undo/Redo */}
        <div className="flex items-center gap-1">
          <button
            onClick={onUndo}
            disabled={!canUndo}
            className="rounded p-1.5 text-muted-foreground hover:bg-muted disabled:text-muted-foreground/30 disabled:hover:bg-transparent"
            title="Undo (Ctrl+Z)"
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12.5 8c-2.65 0-5.05.99-6.9 2.6L2 7v9h9l-3.62-3.62c1.39-1.16 3.16-1.88 5.12-1.88 3.54 0 6.55 2.31 7.6 5.5l2.37-.78C21.08 11.03 17.15 8 12.5 8z" />
            </svg>
          </button>
          <button
            onClick={onRedo}
            disabled={!canRedo}
            className="rounded p-1.5 text-muted-foreground hover:bg-muted disabled:text-muted-foreground/30 disabled:hover:bg-transparent"
            title="Redo (Ctrl+Y)"
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M18.4 10.6C16.55 8.99 14.15 8 11.5 8c-4.65 0-8.58 3.03-9.96 7.22L3.9 16c1.05-3.19 4.05-5.5 7.6-5.5 1.95 0 3.73.72 5.12 1.88L13 16h9V7l-3.6 3.6z" />
            </svg>
          </button>
        </div>

        {/* View tabs */}
        <ViewTabs view={view} onViewChange={onViewChange} compact={isMobile ?? false} />
      </div>

      <div className="flex items-center gap-2 md:gap-3">
        {/* Dirty indicator - hide on mobile to save space */}
        {isDirty && !isMobile && (
          <span className="text-xs text-amber-600 dark:text-amber-400">Unsaved changes</span>
        )}

        {/* Save button */}
        <button
          onClick={onSave}
          disabled={!isDirty}
          className={`rounded-md bg-primary px-3 md:px-4 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:bg-muted disabled:text-muted-foreground disabled:cursor-not-allowed ${isDirty && isMobile ? 'animate-pulse' : ''}`}
        >
          {isMobile ? 'Save' : 'Save to IDF'}
        </button>

        {/* Mobile: Properties toggle */}
        {isMobile && onOpenProperties && (
          <button
            onClick={onOpenProperties}
            className="rounded p-1.5 text-muted-foreground hover:bg-muted"
            title="Properties"
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Create new schedule dialog
 */
function NewScheduleDialog({
  isOpen,
  onClose,
  onCreate,
}: {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (name: string, typeLimits: string | null, type: 'compact' | 'constant') => void;
}) {
  const [name, setName] = useState('New Schedule');
  const [typeLimits, setTypeLimits] = useState<string>('');
  const [scheduleType, setScheduleType] = useState<'compact' | 'constant'>('compact');

  const typeLimitsList = useScheduleStore((state) => state.typeLimits);

  const handleCreate = useCallback(() => {
    if (name.trim()) {
      onCreate(name.trim(), typeLimits || null, scheduleType);
      setName('New Schedule');
      setTypeLimits('');
      onClose();
    }
  }, [name, typeLimits, scheduleType, onCreate, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-lg bg-popover shadow-xl">
        <div className="border-b border-border px-6 py-4">
          <h3 className="text-lg font-semibold text-foreground">New Schedule</h3>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
              autoFocus
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Schedule Type</label>
            <select
              value={scheduleType}
              onChange={(e) => setScheduleType(e.target.value as 'compact' | 'constant')}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
            >
              <option value="compact">Schedule:Compact (variable values)</option>
              <option value="constant">Schedule:Constant (single value)</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Type Limits</label>
            <select
              value={typeLimits}
              onChange={(e) => setTypeLimits(e.target.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
            >
              <option value="">(none)</option>
              {typeLimitsList.map((tl) => (
                <option key={tl.name} value={tl.name}>
                  {tl.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex justify-end gap-3 border-t border-border px-6 py-4">
          <button
            onClick={onClose}
            className="rounded-md border border-border px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-muted"
          >
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={!name.trim()}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:bg-muted disabled:text-muted-foreground"
          >
            Create
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Mobile slide-out drawer component
 */
function MobileDrawer({
  isOpen,
  onClose,
  side,
  title,
  children,
}: {
  isOpen: boolean;
  onClose: () => void;
  side: 'left' | 'right';
  title: string;
  children: React.ReactNode;
}) {
  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50"
      role="dialog"
      aria-modal="true"
      aria-labelledby={`drawer-title-${side}`}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
        aria-hidden="true"
      />
      {/* Drawer */}
      <div
        className={`absolute top-0 bottom-0 w-[85vw] max-w-[320px] bg-card shadow-xl flex flex-col ${
          side === 'left' ? 'left-0' : 'right-0'
        }`}
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <h3 id={`drawer-title-${side}`} className="font-semibold text-foreground">{title}</h3>
          <button
            onClick={onClose}
            className="rounded p-2 text-muted-foreground hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring"
            aria-label="Close drawer"
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {children}
        </div>
      </div>
    </div>
  );
}

/**
 * Constant schedule editor (simple value input)
 */
function ConstantScheduleEditor({
  value,
  onChange,
  typeLimits,
  readOnly = false,
}: {
  value: number;
  onChange: (value: number) => void;
  typeLimits?: { lowerLimit?: number; upperLimit?: number } | null;
  readOnly?: boolean;
}) {
  const minValue = typeLimits?.lowerLimit ?? 0;
  const maxValue = typeLimits?.upperLimit ?? 1;

  return (
    <div className="rounded-lg border border-border bg-card p-6">
      <h3 className="text-sm font-medium text-foreground mb-4">Constant Value Schedule</h3>
      <p className="text-sm text-muted-foreground mb-4">
        This schedule uses a constant value for all hours of every day throughout the year.
      </p>

      <div className="flex items-center gap-4">
        <label className="text-sm font-medium text-foreground" htmlFor="constant-value">
          Value:
        </label>
        <input
          id="constant-value"
          type="number"
          value={value}
          onChange={(e) => {
            const v = parseFloat(e.target.value) || 0;
            onChange(Math.min(maxValue, Math.max(minValue, v)));
          }}
          min={minValue}
          max={maxValue}
          step="0.1"
          disabled={readOnly}
          className="w-32 rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring disabled:bg-muted disabled:text-muted-foreground"
        />
        <span className="text-xs text-muted-foreground">
          (Range: {minValue} - {maxValue})
        </span>
      </div>

      {/* Visual representation */}
      <div className="mt-6 h-16 rounded bg-primary/20 flex items-center justify-center">
        <span className="text-2xl font-semibold text-primary">{value}</span>
      </div>
      <p className="mt-2 text-xs text-muted-foreground text-center">
        This value applies 24/7/365
      </p>
    </div>
  );
}

/**
 * Expandable graph preview
 */
function ExpandableGraph({
  schedule,
  selectedPeriodId,
  typeLimits,
}: {
  schedule: Parameters<typeof ScheduleGraph>[0]['schedule'];
  selectedPeriodId: string | null;
  typeLimits: Parameters<typeof ScheduleGraph>[0]['typeLimits'] | undefined;
}) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="rounded-lg border border-border bg-card overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2 border-b border-border">
        <h3 className="text-sm font-medium text-foreground">Preview</h3>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="rounded p-1.5 text-muted-foreground hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring"
          aria-expanded={isExpanded}
          aria-label={isExpanded ? 'Collapse preview' : 'Expand preview'}
        >
          <svg
            className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden="true"
          >
            <path d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>
      <div className={`transition-all duration-200 ${isExpanded ? 'h-[340px]' : 'h-[220px]'}`}>
        <ScheduleGraph
          schedule={schedule}
          selectedPeriodId={selectedPeriodId}
          typeLimits={typeLimits ?? null}
          height={isExpanded ? 320 : 200}
        />
      </div>
    </div>
  );
}

/**
 * Schedule Editor Page Component
 */
export function ScheduleEditorPage() {
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [showTemplateLibrary, setShowTemplateLibrary] = useState(false);
  const [showBrowserDrawer, setShowBrowserDrawer] = useState(false);
  const [showPropertiesDrawer, setShowPropertiesDrawer] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showDuplicatePrompt, setShowDuplicatePrompt] = useState(false);

  const isMobile = useIsMobile();

  // Store state
  const selectedScheduleId = useScheduleStore((state) => state.selectedScheduleId);
  const selectedPeriodId = useScheduleStore((state) => state.selectedPeriodId);
  const view = useScheduleStore((state) => state.view);
  const isDirty = useScheduleStore((state) => state.isDirty);
  const typeLimits = useScheduleStore((state) => state.typeLimits);

  // Store actions
  const loadFromDocument = useScheduleStore((state) => state.loadFromDocument);
  const selectPeriod = useScheduleStore((state) => state.selectPeriod);
  const setView = useScheduleStore((state) => state.setView);
  const createSchedule = useScheduleStore((state) => state.createSchedule);
  const deleteSchedule = useScheduleStore((state) => state.deleteSchedule);
  const duplicateSchedule = useScheduleStore((state) => state.duplicateSchedule);
  const updateSchedule = useScheduleStore((state) => state.updateSchedule);
  const updateDayValues = useScheduleStore((state) => state.updateDayValues);
  const updatePeriod = useScheduleStore((state) => state.updatePeriod);
  const addPeriod = useScheduleStore((state) => state.addPeriod);
  const deletePeriod = useScheduleStore((state) => state.deletePeriod);
  const applyTemplate = useScheduleStore((state) => state.applyTemplate);
  const undo = useScheduleStore((state) => state.undo);
  const redo = useScheduleStore((state) => state.redo);
  const canUndo = useScheduleStore((state) => state.canUndo);
  const canRedo = useScheduleStore((state) => state.canRedo);
  const syncToDocument = useScheduleStore((state) => state.syncToDocument);

  // IDF store actions for syncing
  const updateObject = useIDFStore((state) => state.updateObject);
  const addObject = useIDFStore((state) => state.addObject);
  const removeObject = useIDFStore((state) => state.removeObject);

  // IDF store
  const document = useIDFStore((state) => state.document);

  // Selected schedule and period
  const selectedSchedule = useSelectedSchedule();
  const selectedPeriod = useSelectedPeriod();

  // Get type limits for selected schedule
  const scheduleTypeLimits = useMemo(() => {
    if (!selectedSchedule?.typeLimits) return null;
    return typeLimits.find((tl) => tl.name === selectedSchedule.typeLimits) ?? null;
  }, [selectedSchedule?.typeLimits, typeLimits]);

  // Load schedules when document changes
  useEffect(() => {
    if (document) {
      loadFromDocument(document);
    }
  }, [document, loadFromDocument]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        e.preventDefault();
        if (e.shiftKey) {
          redo();
        } else {
          undo();
        }
      } else if ((e.ctrlKey || e.metaKey) && e.key === 'y') {
        e.preventDefault();
        redo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [undo, redo]);

  // Handle creating new schedule
  const handleCreateSchedule = useCallback(
    (name: string, typeLimitsName: string | null, type: 'compact' | 'constant') => {
      createSchedule(name, typeLimitsName, type);
    },
    [createSchedule]
  );

  // Handle applying template
  const handleApplyTemplate = useCallback(
    (template: ScheduleTemplate, name: string) => {
      applyTemplate(template, name);
    },
    [applyTemplate]
  );

  // Handle save to IDF
  const handleSave = useCallback(() => {
    // Sync all schedule changes back to the IDF document
    syncToDocument(updateObject, addObject, removeObject);
  }, [syncToDocument, updateObject, addObject, removeObject]);

  // Handle day schedule update
  const handleDaySchedulesChange = useCallback(
    (newDaySchedules: DaySchedule[]) => {
      if (selectedScheduleId && selectedPeriodId) {
        updatePeriod(selectedScheduleId, selectedPeriodId, { daySchedules: newDaySchedules });
      }
    },
    [selectedScheduleId, selectedPeriodId, updatePeriod]
  );

  // Handle adding period
  const handleAddPeriod = useCallback(() => {
    if (selectedScheduleId && selectedSchedule) {
      const lastPeriod = selectedSchedule.periods[selectedSchedule.periods.length - 1];
      addPeriod(selectedScheduleId, {
        startDate: { month: 1, day: 1 },
        endDate: { month: 12, day: 31 },
        daySchedules: lastPeriod?.daySchedules || [{ dayTypes: ['AllDays'], values: [{ hour: 24, minute: 0, value: 1 }] }],
      });
    }
  }, [selectedScheduleId, selectedSchedule, addPeriod]);

  // Handle delete schedule
  const handleDeleteSchedule = useCallback(() => {
    if (selectedScheduleId) {
      setShowDeleteConfirm(true);
    }
  }, [selectedScheduleId]);

  // Actually delete after confirmation
  const confirmDeleteSchedule = useCallback(() => {
    if (selectedScheduleId) {
      deleteSchedule(selectedScheduleId);
    }
  }, [selectedScheduleId, deleteSchedule]);

  // Handle duplicate schedule
  const handleDuplicateSchedule = useCallback(() => {
    if (selectedSchedule) {
      setShowDuplicatePrompt(true);
    }
  }, [selectedSchedule]);

  // Actually duplicate after prompt
  const confirmDuplicateSchedule = useCallback(
    (newName: string) => {
      if (selectedSchedule) {
        duplicateSchedule(selectedSchedule.id, newName);
      }
    },
    [selectedSchedule, duplicateSchedule]
  );

  return (
    <div className="flex h-full bg-background">
      {/* Left sidebar - Schedule Browser (desktop only) */}
      {!isMobile && (
        <ResizablePanel
          side="left"
          defaultWidth={256}
          minWidth={180}
          maxWidth={400}
          storageKey="schedule-browser-panel"
          className="overflow-y-auto"
          hideOnMobile={false}
          resizeAriaLabel="Resize schedule browser panel"
        >
          <ScheduleBrowser
            onNewSchedule={() => setShowNewDialog(true)}
            onImportTemplate={() => setShowTemplateLibrary(true)}
          />
        </ResizablePanel>
      )}

      {/* Center - Editor */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Toolbar */}
        <Toolbar
          canUndo={canUndo()}
          canRedo={canRedo()}
          onUndo={undo}
          onRedo={redo}
          view={view}
          onViewChange={setView}
          isDirty={isDirty}
          onSave={handleSave}
          isMobile={isMobile}
          onOpenBrowser={() => setShowBrowserDrawer(true)}
          onOpenProperties={() => setShowPropertiesDrawer(true)}
        />

        {/* Editor content */}
        <div className="flex-1 overflow-y-auto bg-muted/30 p-3 md:p-6" role="tabpanel" id={`${view}-panel`}>
          {selectedSchedule ? (
            <div className="space-y-6">
              {/* Check if this is a constant schedule */}
              {selectedSchedule.scheduleType === 'Schedule:Constant' ? (
                <ConstantScheduleEditor
                  value={selectedSchedule.constantValue ?? 1}
                  onChange={(value) => {
                    if (selectedScheduleId) {
                      updateSchedule(selectedScheduleId, { constantValue: value });
                    }
                  }}
                  typeLimits={scheduleTypeLimits}
                />
              ) : (
                <>
                  {/* Graph preview */}
                  <ExpandableGraph
                    schedule={selectedSchedule}
                    selectedPeriodId={selectedPeriodId}
                    typeLimits={scheduleTypeLimits}
                  />

                  {/* View-specific editor */}
                  {view === 'day' && selectedPeriod && (
                    <DayEditor
                      values={selectedPeriod.daySchedules[0]?.values || []}
                      dayTypes={selectedPeriod.daySchedules[0]?.dayTypes || ['AllDays']}
                      onChange={(values) => {
                        if (selectedScheduleId && selectedPeriodId) {
                          const dayTypes = selectedPeriod.daySchedules[0]?.dayTypes || ['AllDays'];
                          updateDayValues(selectedScheduleId, selectedPeriodId, dayTypes, values);
                        }
                      }}
                      typeLimits={scheduleTypeLimits}
                    />
                  )}

                  {view === 'week' && selectedPeriod && (
                    <WeekEditor
                      daySchedules={selectedPeriod.daySchedules}
                      onChange={handleDaySchedulesChange}
                      typeLimits={scheduleTypeLimits}
                    />
                  )}

                  {view === 'year' && (
                    <YearEditor
                      periods={selectedSchedule.periods}
                      selectedPeriodId={selectedPeriodId}
                      onSelectPeriod={selectPeriod}
                      onAddPeriod={handleAddPeriod}
                      onDeletePeriod={(periodId) => {
                        if (selectedScheduleId) {
                          deletePeriod(selectedScheduleId, periodId);
                        }
                      }}
                      onUpdatePeriod={(periodId, updates) => {
                        if (selectedScheduleId) {
                          updatePeriod(selectedScheduleId, periodId, updates);
                        }
                      }}
                    />
                  )}
                </>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 md:py-24 px-4">
              <svg
                className="h-16 w-16 md:h-20 md:w-20 text-muted-foreground/30"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
              >
                <rect x="3" y="4" width="18" height="18" rx="2" />
                <path d="M16 2v4M8 2v4M3 10h18M8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h.01M16 18h.01" />
              </svg>
              <h2 className="mt-4 text-base md:text-lg font-medium text-foreground text-center">No Schedule Selected</h2>
              <p className="mt-1 text-sm text-muted-foreground text-center">
                {isMobile ? 'Tap the menu to browse schedules' : 'Select a schedule from the browser panel'}
              </p>
              {/* Only show action buttons on mobile where browser is hidden */}
              {isMobile && (
                <button
                  onClick={() => setShowBrowserDrawer(true)}
                  className="mt-6 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
                >
                  Browse Schedules
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Right sidebar - Properties (desktop only) */}
      {!isMobile && (
        <ResizablePanel
          side="right"
          defaultWidth={288}
          minWidth={200}
          maxWidth={450}
          storageKey="schedule-properties-panel"
          className="overflow-y-auto"
          hideOnMobile={false}
          resizeAriaLabel="Resize schedule properties panel"
        >
          <SchedulePropertiesPanel
            schedule={selectedSchedule ?? null}
            onUpdate={(updates) => {
              if (selectedScheduleId) {
                updateSchedule(selectedScheduleId, updates);
              }
            }}
            onDelete={handleDeleteSchedule}
            onDuplicate={handleDuplicateSchedule}
          />
        </ResizablePanel>
      )}

      {/* Mobile Drawers */}
      {isMobile && (
        <>
          <MobileDrawer
            isOpen={showBrowserDrawer}
            onClose={() => setShowBrowserDrawer(false)}
            side="left"
            title="Schedules"
          >
            <ScheduleBrowser
              onNewSchedule={() => {
                setShowBrowserDrawer(false);
                setShowNewDialog(true);
              }}
              onImportTemplate={() => {
                setShowBrowserDrawer(false);
                setShowTemplateLibrary(true);
              }}
              onScheduleSelect={() => setShowBrowserDrawer(false)}
            />
          </MobileDrawer>

          <MobileDrawer
            isOpen={showPropertiesDrawer}
            onClose={() => setShowPropertiesDrawer(false)}
            side="right"
            title="Properties"
          >
            <SchedulePropertiesPanel
              schedule={selectedSchedule ?? null}
              onUpdate={(updates) => {
                if (selectedScheduleId) {
                  updateSchedule(selectedScheduleId, updates);
                }
              }}
              onDelete={() => {
                handleDeleteSchedule();
                setShowPropertiesDrawer(false);
              }}
              onDuplicate={() => {
                handleDuplicateSchedule();
                setShowPropertiesDrawer(false);
              }}
            />
          </MobileDrawer>
        </>
      )}

      {/* Dialogs */}
      <NewScheduleDialog
        isOpen={showNewDialog}
        onClose={() => setShowNewDialog(false)}
        onCreate={handleCreateSchedule}
      />

      <TemplateLibrary
        isOpen={showTemplateLibrary}
        onClose={() => setShowTemplateLibrary(false)}
        onSelect={handleApplyTemplate}
      />

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={confirmDeleteSchedule}
        title="Delete Schedule"
        message={`Are you sure you want to delete "${selectedSchedule?.name}"? This action cannot be undone.`}
        confirmLabel="Delete"
        confirmVariant="destructive"
      />

      <PromptDialog
        isOpen={showDuplicatePrompt}
        onClose={() => setShowDuplicatePrompt(false)}
        onSubmit={confirmDuplicateSchedule}
        title="Duplicate Schedule"
        label="New schedule name"
        defaultValue={selectedSchedule ? `${selectedSchedule.name} (copy)` : ''}
        submitLabel="Duplicate"
      />
    </div>
  );
}
